import { PayloadAction } from '@reduxjs/toolkit';
import { message } from 'antd';
import { FiltersType2 } from 'features/Allocation/AllocationListTable/AllocatedListTable';
import { delay, put, takeLatest } from 'redux-saga/effects';
import {
  allocationEngineEndpoints,
  localHostName,
  manageAgency,
} from 'services/api/endPoints';
import { HTTPClient } from 'services/api/http-client';
import { AllocationType } from 'testData/allocationLIst';

import {
  fetchAllocation,
  fetchAllocationFailure,
  fetchAllocationOverviewAction,
  fetchAllocationOverviewSuccessAction,
  fetchAllocationSuccess,
  fetchGroupedAllocationNumbers,
  fetchGroupedAllocationNumbersFailure,
  fetchGroupedAllocationNumbersSuccess,
  setLoadingFalse,
} from './allocation.slice';
import {
  FetchAllocationListResponse,
  FetchAllocationOverViewResponse,
  FetchGroupedAllocationNumbersResponse,
  IAgencyApiResponse,
} from './types';

export type FetchAllocationRequestType = {
  allocationStatus: string;
  assignmentType: string;
  limit: number;
  offset: number;
  lenderId: string;
  filterRequestPayload: FiltersType2[];
};

function* fetchAllocationDetailsSaga(
  action: PayloadAction<FetchAllocationRequestType>
) {
  try {
    let agencyList: any[] = [];

    if (
      action.payload.allocationStatus !== 'unallocated' &&
      action.payload.assignmentType !== 'Self'
    ) {
      try {
        const result: IAgencyApiResponse = yield HTTPClient.PostRequest(
          manageAgency.GET_AGENCY_LIST
        );

        const { data, status } = result;

        const uniqueValues = new Set(); // Set to store unique values
        agencyList = data.data.reduce((acc: any[], inputValue: any) => {
          const name: string =
            inputValue.collectionAgency.entityInfo.entityName;
          const value: string = inputValue.collectionAgency.entityID;
          const newItem = { label: name, value };
          if (!uniqueValues.has(value)) {
            uniqueValues.add(value);
            acc.push(newItem); // Add unique item to the accumulator array
          }
          return acc;
        }, []);
      } catch (e) {}
    }

    const result: FetchAllocationListResponse = yield HTTPClient.PostRequest(
      allocationEngineEndpoints.GET_ALLOCATION_LIST(action.payload.lenderId),
      {
        limit: action.payload.limit,
        offset: action.payload.offset,
        allocationStatus: action.payload.allocationStatus,
        assignmentType: action.payload.assignmentType,
        filters: action.payload.filterRequestPayload,
      }
    );
    const allocationType = action.payload
      .allocationStatus as keyof typeof result.data.groupedAllocations;
    const allocation = result.data.groupedAllocations[allocationType];

    const assignmentType2 = action.payload
      .assignmentType as keyof typeof allocation;

    const allocations: AllocationType[] =
      result.data.groupedAllocations[allocationType][assignmentType2];

    const mappedAllocations = allocations.map((allocation: any) => {
      const entity = agencyList.find(
        (entity) => entity.value === allocation.assignedToEntity
      );
      if (entity) {
        return {
          ...allocation,
          assignedToEntityLabel: entity.label, // Add assignedToEntityLabel property
        };
      }

      if (action.payload.assignmentType === 'Self') {
        return {
          ...allocation,
          assignedToEntityLabel: 'Self', // Add assignedToEntityLabel property
        };
      }
      return allocation;
    });

    yield put(
      fetchAllocationSuccess({
        allocatedData: mappedAllocations,
      })
    );
  } catch (e) {
    yield put(fetchAllocationFailure());
  }
}
//

function* fetchAllocationOverViewSaga() {
  try {
    yield delay(1000);
    const result: FetchAllocationOverViewResponse = yield HTTPClient.GetRequest(
      `${localHostName}/allocationOverview`
    );
    yield put(
      fetchAllocationOverviewSuccessAction({ allocationOverview: result.data })
    );
  } catch (e) {
    message.error('Error while fetching allocation overview details');
    yield put(setLoadingFalse());
  }
}

function* fetchGroupedAllocationNumbersSaga(
  action: PayloadAction<{
    lenderId: string;
    filterRequestPayload: FiltersType2[];
  }>
) {
  try {
    const result: FetchGroupedAllocationNumbersResponse =
      yield HTTPClient.PostRequest(
        allocationEngineEndpoints.GET_GROUPED_ALLOCATIONS_NUMBERS(
          action.payload.lenderId
        ),
        action.payload.filterRequestPayload
      );
    yield put(
      fetchGroupedAllocationNumbersSuccess({
        groupedAllocationNumbers: result.data.groupedAllocationNumbers,
      })
    );
  } catch (e) {
    yield put(fetchGroupedAllocationNumbersFailure());
  }
}

export function* watchAllocationSagas() {
  yield takeLatest(fetchAllocation.type, fetchAllocationDetailsSaga);
  yield takeLatest(
    fetchAllocationOverviewAction.type,
    fetchAllocationOverViewSaga
  );
  yield takeLatest(
    fetchGroupedAllocationNumbers.type,
    fetchGroupedAllocationNumbersSaga
  );
}
